import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {logger} from '../../../../libs/shared/core/src/services/logger/logger';
import {reportToBugsnag} from '../../helpers/bugsnag';
import {postSurvey} from '../../helpers/http';
import {CompanyLogo} from '../common/companyLogo';
import {ConfigurableLayer} from '../common/configurableLayer';
import {BannerError} from '../common/error/bannerError';
import {FrontPower} from '../common/frontPower';
import {Loader} from '../common/loader';
import {MainContentWrapper} from '../common/mainContentWrapper';
import {useUserDefinedSurveyConfig} from '../common/userDefinedSurvey/userDefinedSurveyConfig';
import {SurveyForm} from './surveyForm';
import {useSurveyRouterData} from './surveyRoutesHelpers';
import {SurveySubmitted} from './surveySubmitted';

/*
 * Constants.
 */

const csatLogger = logger.getLogger('csat');

/*
 * Types.
 */

enum SurveyErrorsEnum {
  ALREADY_SUBMITTED = 'Survey already submitted',
  SUBMIT_FAILED = 'Failed sending feedback, please try again later',
}

/*
 * Style.
 */

const SurveyPageContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: 20px;
`;

/*
 * Components.
 */

export const SurveyPage: FC = () => {
  const routerData = useSurveyRouterData();
  const [surveyState, setSurveyState] = useState<{submitted: true; score: number} | {submitted: false}>({
    submitted: false,
  });
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const dismissError = () => setError(undefined);
  const isPreview = !routerData.msgUid;
  const {isLoading, config} = useUserDefinedSurveyConfig(isPreview);

  const onFormSubmit = async (score: number, note?: string, contact?: string) => {
    setIsFormSubmitting(true);
    try {
      await postSurvey(routerData, score, note, contact);
      setSurveyState({submitted: true, score});
    } catch (err) {
      if (err.response?.status === 403) {
        setError(SurveyErrorsEnum.ALREADY_SUBMITTED);
      } else {
        setError(SurveyErrorsEnum.SUBMIT_FAILED);
        csatLogger.error('Failed to submit survey', {
          error_code: err.code,
          error_message: err.message,
          error_data: err.response?.data,
        });
        reportToBugsnag(
          new Error(`Failed to submit survey: ${err.code} ${err.message} - ${err.response?.data?.reason}`),
        );
      }
    }
    setIsFormSubmitting(false);
  };

  return (
    <ConfigurableLayer backgroundColor={config.primaryColor}>
      {isLoading && <Loader color="rgb(6,26,54)" />}
      {!isLoading && (
        <SurveyPageContentDiv>
          <CompanyLogo companyLogoUrl={config.companyLogo?.url} />
          <MainContentWrapper>
            {maybeRenderError(dismissError, error)}
            {surveyState.submitted ? (
              <SurveySubmitted secondaryColor={config.secondaryColor} stars={surveyState.score} />
            ) : (
              <SurveyForm
                routeScore={routerData.score}
                includeContactInput={routerData.includeContactInput ?? false}
                submitForm={onFormSubmit}
                isFormSubmitting={isFormSubmitting}
                surveyPrompt={config.surveyPrompt}
                secondaryColor={config.secondaryColor}
                answerFieldPlaceholderText={config.answerFieldPlaceholderText}
                buttonText={config.buttonText}
                primaryButtonText={config.primaryButtonText}
                isPreview={isPreview}
              />
            )}
          </MainContentWrapper>
          {config.shouldDisplayPoweredByFront && <FrontPower />}
        </SurveyPageContentDiv>
      )}
    </ConfigurableLayer>
  );
};

/*
 * Helpers.
 */

function maybeRenderError(dismissError: () => void, error?: string) {
  if (!error) {
    return null;
  }

  return <BannerError description={error} onDismiss={dismissError} />;
}
